import { getEnvConfig } from '@hbs-us/contracts';
import { useTranslation } from '@hbs-us/ui';
import { AlertTriangle, CalendarClock } from 'lucide-react';
import { useEffect, useState } from 'react';

import { getAppEnv } from '../../lib/env.js';

type ComponentState = 'operational' | 'degraded' | 'partial_outage' | 'major_outage' | 'maintenance';

interface PublicStatusResponse {
  overallStatus?: ComponentState;
  incidents?: unknown[];
  maintenance?: unknown[];
}

function publicStatusUrl(): string {
  return `https://${getEnvConfig(getAppEnv()).domains.api}/api/v1/status/public`;
}

function statusHost(): string {
  const env = getAppEnv();
  return env === 'prod' ? 'https://status.qelm.app' : `https://status.${env}.qelm.app`;
}

export function StatusBanner() {
  const { t } = useTranslation('nav');
  const [status, setStatus] = useState<PublicStatusResponse | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 2500);
    fetch(publicStatusUrl(), {
      credentials: 'omit',
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => setStatus(body as PublicStatusResponse | null))
      .catch(() => setStatus(null))
      .finally(() => window.clearTimeout(timeout));
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  if (!status) return null;

  const overall = status.overallStatus ?? 'operational';
  const hasMaintenance = Array.isArray(status.maintenance) && status.maintenance.length > 0;
  const hasIncidents = Array.isArray(status.incidents) && status.incidents.length > 0;
  if (overall === 'operational' && !hasMaintenance && !hasIncidents) return null;

  const isMaintenance = overall === 'maintenance' || hasMaintenance;
  const Icon = isMaintenance ? CalendarClock : AlertTriangle;
  const label = isMaintenance
    ? t('status.maintenance')
    : overall === 'degraded'
      ? t('status.degraded')
      : t('status.incident');

  return (
    <aside
      role="status"
      aria-live="polite"
      className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-border border-b bg-card px-4 py-3 text-card-foreground sm:px-6"
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="inline-flex size-9 items-center justify-center rounded-full bg-accent text-accent-foreground">
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="font-semibold text-sm">{label}</p>
          <p className="text-muted-foreground text-xs">{t('status.body')}</p>
        </div>
      </div>
      <a
        href={statusHost()}
        rel="noopener noreferrer"
        className="rounded-md border border-border px-3 py-2 font-medium text-sm transition-colors hover:bg-sidebar-accent/60"
      >
        {t('status.view')}
      </a>
    </aside>
  );
}
